import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InputNumber, Select, Space, Switch, Typography, message } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import RecipientSourceNote from '@/components/team-assignments/RecipientSourceNote';
import { API } from '@/services/api.service';
import Fold from './Fold';

const { Text } = Typography;

const when = (value) => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—');

/**
 * Settings of the daily "approved on SEF vs booked in FIPO" table: for which
 * company, to whom, at what hour — next to the AB pull warnings, because the
 * table is only as fresh as the last pull.
 *
 * @returns {JSX.Element} The card
 */
export default function ErpBookingReportCard() {
  const { t } = useTranslation('clientReports');
  const tb = (key, options) => t(`bookingReport.${key}`, options);
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['client-reports-erp-booking-report'],
    queryFn: () => API.clientReports.getErpBookingReport(),
    refetchOnWindowFocus: false,
  });
  const settings = data?.data;

  const [form, setForm] = useState(null);
  useEffect(() => {
    if (settings && !form) {
      setForm({
        enabled: settings.enabled,
        company: settings.company ? String(settings.company) : null,
        recipients: settings.recipients || [],
        sendHour: settings.sendHour,
      });
    }
  }, [settings, form]);

  const errorOf = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

  const save = useMutation({
    mutationFn: (values) => API.clientReports.saveErpBookingReport(values),
    onSuccess: () => {
      message.success(tb('saved'));
      queryClient.invalidateQueries({ queryKey: ['client-reports-erp-booking-report'] });
    },
    onError: (e) => message.error(errorOf(e, tb('saveError'))),
  });

  const test = useMutation({
    mutationFn: () => API.clientReports.testErpBookingReport(),
    onSuccess: (res) => {
      const result = res?.data || {};
      if (result.sent) {
        message.success(
          tb('testSent', {
            list: (result.recipients || []).join(', '),
            rows: result.rows,
            notBooked: result.notBooked,
          })
        );
      } else {
        message.warning(tb('testNotSent'));
      }
      queryClient.invalidateQueries({ queryKey: ['client-reports-erp-booking-report'] });
    },
    onError: (e) => message.error(errorOf(e, tb('testError'))),
  });

  if (!form) return null;

  const [year, month] = String(settings?.period || '').split('-');

  return (
    <Fold title={tb('title')}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {tb('hint')}
        </Text>

        <Space size={12} wrap>
          <Switch checked={form.enabled} onChange={(enabled) => setForm({ ...form, enabled })} />
          <Text>{tb('enabled')}</Text>
        </Space>

        <div style={{ maxWidth: 520 }}>
          <Text style={{ fontSize: 12 }}>{tb('company')}</Text>
          <Select
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp="label"
            options={settings?.companies || []}
            value={form.company}
            onChange={(company) => setForm({ ...form, company: company || null })}
          />
        </div>

        <div style={{ maxWidth: 520 }}>
          {form.company && String(settings?.company) === form.company && (
            <RecipientSourceNote companyId={form.company} section="BOOKKEEPING" />
          )}
          <Text style={{ fontSize: 12 }}>{tb('recipients')}</Text>
          <Select
            mode="tags"
            style={{ width: '100%' }}
            tokenSeparators={[',', ' ', ';']}
            value={form.recipients}
            onChange={(recipients) => setForm({ ...form, recipients })}
            placeholder="borislav@hlb.rs"
          />
        </div>

        <Space size={8}>
          <Text style={{ fontSize: 12 }}>{tb('sendHour')}</Text>
          <InputNumber
            min={0}
            max={23}
            value={form.sendHour}
            onChange={(sendHour) => setForm({ ...form, sendHour })}
          />
        </Space>

        <Space size={8} wrap>
          <Button className="btn-primary" loading={save.isPending} onClick={() => save.mutate(form)}>
            {tb('save')}
          </Button>
          <Button
            shape="round"
            icon={<SendOutlined />}
            loading={test.isPending}
            disabled={!settings?.company || !settings?.recipients?.length}
            onClick={() => test.mutate()}
          >
            {tb('sendNow')}
          </Button>
        </Space>

        <Text type="secondary" style={{ fontSize: 12 }}>
          {tb('status', {
            period: month && year ? `${month}/${year}` : '—',
            sent: when(settings?.lastSentAt),
          })}
        </Text>
      </Space>
    </Fold>
  );
}
